import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../supabaseClient';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    loanAmount: '',
    loanPurpose: '',
    creditScore: '',
    message: '',
    agreeToShare: false,
    agreeToSMS: false,
    agreeToTCPA: false
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Submit to Supabase
      const { data, error } = await supabase
        .from('cargo_engage_leads')
        .insert([{
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          loan_amount: formData.loanAmount,
          loan_purpose: formData.loanPurpose,
          credit_score: formData.creditScore,
          message: formData.message,
          agree_to_share: formData.agreeToShare,
          agree_to_sms: formData.agreeToSMS,
          agree_to_tcpa: formData.agreeToTCPA,
          status: 'new',
          source: 'cargo_engage',
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      // Success
      setSubmitMessage('✓ Request submitted! A lending partner will contact you within 24 hours.');
      setCurrentStep(1);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        loanAmount: '',
        loanPurpose: '',
        creditScore: '',
        message: '',
        agreeToShare: false,
        agreeToSMS: false,
        agreeToTCPA: false
      });
    } catch (error) {
      // Error handling
      setSubmitMessage('❌ There was an error submitting your request. Please try again or contact support@cargoengage.com');
      console.error('Supabase Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{background: '#f8fafc', minHeight: '100vh', paddingTop: '100px'}}>
      <Helmet>
        <title>Contact Us - Find Financial Solutions | Cargo Engage</title>
        <meta name="description" content="Connect with trusted financial service providers. Submit your loan inquiry and get matched with lending partners. TCPA compliant lead generation with express written consent." />
        <meta name="keywords" content="loan inquiry, financial services, lending partners, mortgage application, personal loan, business financing" />
      </Helmet>
      {/* Hero Section */}
      <section style={{background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', padding: '4rem 0', color: 'white'}}>
        <div className="container">
          <div style={{maxWidth: '700px', margin: '0 auto', textAlign: 'center'}}>
            <h1 style={{fontSize: '3rem', fontWeight: '800', marginBottom: '1rem', fontFamily: 'Space Grotesk'}}>
              Find Your Financial Solution
            </h1>
            <p style={{fontSize: '1.25rem', color: 'rgba(255,255,255,0.7)'}}>
              Complete the form below and we'll connect you with trusted financial service providers from our network
            </p>
          </div>
        </div>
      </section>

      {/* Multi-Step Form */}
      <section style={{padding: '4rem 0'}}>
        <div className="container">
          <div style={{maxWidth: '900px', margin: '0 auto'}}>
            
            {/* Progress Bar */}
            <div style={{marginBottom: '3rem'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '1rem'}}>
                {['Personal Info', 'Loan Details', 'Consent'].map((step, index) => (
                  <div key={index} style={{flex: 1, textAlign: 'center'}}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: currentStep > index ? 'linear-gradient(135deg, #06b6d4, #0891b2)' : '#e2e8f0',
                      color: currentStep > index ? 'white' : '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      fontWeight: '700',
                      fontSize: '1.125rem'
                    }}>
                      {index + 1}
                    </div>
                    <div style={{fontSize: '0.875rem', marginTop: '0.5rem', color: currentStep > index ? '#06b6d4' : '#64748b', fontWeight: '600'}}>
                      {step}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{height: '4px', background: '#e2e8f0', borderRadius: '2px', position: 'relative'}}>
                <div style={{
                  height: '100%',
                  background: 'linear-gradient(90deg, #06b6d4, #0891b2)',
                  borderRadius: '2px',
                  width: `${(currentStep / 3) * 100}%`,
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>

            {/* Disclosure Banner */}
            <div style={{background: '#fef3c7', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem', border: '2px solid #fbbf24'}}>
              <div style={{display: 'flex', gap: '1rem', alignItems: 'start'}}>
                <div style={{fontSize: '1.5rem'}}>⚠️</div>
                <div>
                  <h3 style={{color: '#92400e', fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem'}}>Important Information</h3>
                  <p style={{color: '#78350f', fontSize: '0.95rem', lineHeight: '1.6', margin: 0}}>
                    CARGO AUTOMOTIVE SERVICES INC (operating as Cargo Engage) is a <strong>lead generation platform</strong>. We are <strong>NOT a lender</strong>. 
                    We connect you with financial service providers who may contact you about loan products.
                  </p>
                </div>
              </div>
            </div>

            {submitMessage && (
              <div style={{background: '#d1fae5', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem', border: '2px solid #10b981', color: '#065f46'}}>
                {submitMessage}
              </div>
            )}

            {/* Form Card */}
            <div style={{background: 'white', borderRadius: '16px', padding: '3rem', boxShadow: '0 4px 20px rgba(0,0,0,0.08)'}}>
              <form onSubmit={handleSubmit}>
                
                {/* Step 1: Personal Info */}
                {currentStep === 1 && (
                  <div>
                    <h2 style={{fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: '#0f172a'}}>
                      Tell us about yourself
                    </h2>
                    
                    <div style={{marginBottom: '1.5rem'}}>
                      <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#334155'}}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        style={{
                          width: '100%',
                          padding: '0.875rem 1rem',
                          border: '2px solid #e2e8f0',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#06b6d4'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                      />
                    </div>

                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem'}}>
                      <div>
                        <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#334155'}}>
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@example.com"
                          style={{
                            width: '100%',
                            padding: '0.875rem 1rem',
                            border: '2px solid #e2e8f0',
                            borderRadius: '10px',
                            fontSize: '1rem'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#06b6d4'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                      </div>

                      <div>
                        <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#334155'}}>
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="(555) 123-4567"
                          style={{
                            width: '100%',
                            padding: '0.875rem 1rem',
                            border: '2px solid #e2e8f0',
                            borderRadius: '10px',
                            fontSize: '1rem'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#06b6d4'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                    </div>

                    {/* TCPA Consent - Required at time of phone collection */}
                    <div style={{background: '#f0f9ff', padding: '1.5rem', borderRadius: '12px', marginBottom: '1.5rem', border: '2px solid #3b82f6'}}>
                      <h3 style={{fontSize: '1rem', fontWeight: '700', marginBottom: '1rem', color: '#1e40af'}}>
                        Required Consent
                      </h3>
                      
                      <div style={{marginBottom: '1rem'}}>
                        <label style={{display: 'flex', alignItems: 'start', gap: '0.75rem', cursor: 'pointer'}}>
                          <input
                            type="checkbox"
                            name="agreeToSMS"
                            checked={formData.agreeToSMS}
                            onChange={handleChange}
                            required
                            style={{marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0}}
                          />
                          <span style={{fontSize: '0.9rem', lineHeight: '1.6', color: '#1e40af'}}>
                            <strong>SMS Consent:</strong> I consent to receive text messages from Cargo Engage and lending partners at the phone number provided. Message and data rates may apply. Reply STOP to opt out. <strong>Consent is not required as a condition of purchasing any goods or services or to obtain credit.</strong>
                          </span>
                        </label>
                      </div>

                      <div>
                        <label style={{display: 'flex', alignItems: 'start', gap: '0.75rem', cursor: 'pointer'}}>
                          <input
                            type="checkbox"
                            name="agreeToTCPA"
                            checked={formData.agreeToTCPA}
                            onChange={handleChange}
                            required
                            style={{marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0}}
                          />
                          <span style={{fontSize: '0.9rem', lineHeight: '1.6', color: '#1e40af'}}>
                            <strong>TCPA Consent:</strong> I authorize Cargo Engage and lending partners to contact me via phone (including autodialed/pre-recorded calls), email, and SMS at the contact information provided, even if on a Do Not Call list.
                          </span>
                        </label>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      disabled={!formData.agreeToSMS || !formData.agreeToTCPA}
                      style={{
                        width: '100%',
                        padding: '1rem',
                        background: (!formData.agreeToSMS || !formData.agreeToTCPA) ? '#94a3b8' : 'linear-gradient(135deg, #06b6d4, #0891b2)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '1.125rem',
                        fontWeight: '700',
                        cursor: (!formData.agreeToSMS || !formData.agreeToTCPA) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        opacity: (!formData.agreeToSMS || !formData.agreeToTCPA) ? 0.6 : 1
                      }}
                    >
                      Continue →
                    </button>
                  </div>
                )}

                {/* Step 2: Loan Details */}
                {currentStep === 2 && (
                  <div>
                    <h2 style={{fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: '#0f172a'}}>
                      What are you looking for?
                    </h2>

                    <div style={{marginBottom: '1.5rem'}}>
                      <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#334155'}}>
                        Loan Amount Needed
                      </label>
                      <select
                        name="loanAmount"
                        value={formData.loanAmount}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '0.875rem 1rem',
                          border: '2px solid #e2e8f0',
                          borderRadius: '10px',
                          fontSize: '1rem'
                        }}
                      >
                        <option value="">Select amount range</option>
                        <option value="under-10k">Under $10,000</option>
                        <option value="10k-25k">$10,000 - $25,000</option>
                        <option value="25k-50k">$25,000 - $50,000</option>
                        <option value="50k-100k">$50,000 - $100,000</option>
                        <option value="100k-250k">$100,000 - $250,000</option>
                        <option value="over-250k">Over $250,000</option>
                      </select>
                    </div>

                    <div style={{marginBottom: '1.5rem'}}>
                      <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#334155'}}>
                        Loan Purpose
                      </label>
                      <select
                        name="loanPurpose"
                        value={formData.loanPurpose}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '0.875rem 1rem',
                          border: '2px solid #e2e8f0',
                          borderRadius: '10px',
                          fontSize: '1rem'
                        }}
                      >
                        <option value="">Select purpose</option>
                        <option value="home-purchase">Home Purchase</option>
                        <option value="refinance">Refinance</option>
                        <option value="personal">Personal Loan</option>
                        <option value="business">Business Financing</option>
                        <option value="auto">Auto Loan</option>
                        <option value="debt-consolidation">Debt Consolidation</option>
                      </select>
                    </div>

                    <div style={{marginBottom: '1.5rem'}}>
                      <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#334155'}}>
                        Estimated Credit Score
                      </label>
                      <select
                        name="creditScore"
                        value={formData.creditScore}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '0.875rem 1rem',
                          border: '2px solid #e2e8f0',
                          borderRadius: '10px',
                          fontSize: '1rem'
                        }}
                      >
                        <option value="">Select range</option>
                        <option value="excellent">Excellent (750+)</option>
                        <option value="good">Good (700-749)</option>
                        <option value="fair">Fair (650-699)</option>
                        <option value="poor">Poor (Below 650)</option>
                        <option value="unknown">Not Sure</option>
                      </select>
                    </div>

                    <div style={{display: 'flex', gap: '1rem'}}>
                      <button
                        type="button"
                        onClick={() => setCurrentStep(1)}
                        style={{
                          flex: 1,
                          padding: '1rem',
                          background: '#e2e8f0',
                          color: '#334155',
                          border: 'none',
                          borderRadius: '10px',
                          fontSize: '1.125rem',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        ← Back
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentStep(3)}
                        style={{
                          flex: 2,
                          padding: '1rem',
                          background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          fontSize: '1.125rem',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        Continue →
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Consent */}
                {currentStep === 3 && (
                  <div>
                    <h2 style={{fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: '#0f172a'}}>
                      Review & Authorize
                    </h2>

                    <div style={{background: '#f1f5f9', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem'}}>
                      <h3 style={{fontSize: '1.125rem', fontWeight: '700', marginBottom: '1rem', color: '#0f172a'}}>
                        Required Authorizations
                      </h3>
                      
                      <div style={{marginBottom: '1rem'}}>
                        <label style={{display: 'flex', alignItems: 'start', gap: '0.75rem', cursor: 'pointer'}}>
                          <input
                            type="checkbox"
                            name="agreeToShare"
                            checked={formData.agreeToShare}
                            onChange={handleChange}
                            required
                            style={{marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.95rem', lineHeight: '1.6', color: '#334155'}}>
                            <strong>Information Sharing:</strong> I authorize CARGO AUTOMOTIVE SERVICES INC (Cargo Engage) to share my information with financial service providers and lending partners in their network.
                          </span>
                        </label>
                      </div>

                      <div style={{marginBottom: '1rem'}}>
                        <label style={{display: 'flex', alignItems: 'start', gap: '0.75rem', cursor: 'pointer'}}>
                          <input
                            type="checkbox"
                            name="agreeToSMS"
                            checked={formData.agreeToSMS}
                            onChange={handleChange}
                            required
                            style={{marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.95rem', lineHeight: '1.6', color: '#334155'}}>
                            <strong>SMS Consent:</strong> I consent to receive text messages. Message rates may apply. Reply STOP to opt out. <strong>Consent not required for credit.</strong>
                          </span>
                        </label>
                      </div>

                      <div>
                        <label style={{display: 'flex', alignItems: 'start', gap: '0.75rem', cursor: 'pointer'}}>
                          <input
                            type="checkbox"
                            name="agreeToTCPA"
                            checked={formData.agreeToTCPA}
                            onChange={handleChange}
                            required
                            style={{marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer'}}
                          />
                          <span style={{fontSize: '0.95rem', lineHeight: '1.6', color: '#334155'}}>
                            <strong>TCPA Consent:</strong> I consent to be contacted via phone, email, and SMS, even if on a Do Not Call list.
                          </span>
                        </label>
                      </div>
                    </div>

                    <div style={{display: 'flex', gap: '1rem'}}>
                      <button
                        type="button"
                        onClick={() => setCurrentStep(2)}
                        style={{
                          flex: 1,
                          padding: '1rem',
                          background: '#e2e8f0',
                          color: '#334155',
                          border: 'none',
                          borderRadius: '10px',
                          fontSize: '1.125rem',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        ← Back
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                          flex: 2,
                          padding: '1rem',
                          background: isSubmitting ? '#94a3b8' : 'linear-gradient(135deg, #06b6d4, #0891b2)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          fontSize: '1.125rem',
                          fontWeight: '700',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Request ✓'}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Trust Indicators */}
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginTop: '3rem'}}>
              <div style={{textAlign: 'center', padding: '1.5rem', background: 'white', borderRadius: '12px'}}>
                <div style={{fontSize: '2rem', marginBottom: '0.5rem'}}>🔒</div>
                <div style={{fontSize: '0.875rem', fontWeight: '600', color: '#0f172a'}}>Secure & Encrypted</div>
              </div>
              <div style={{textAlign: 'center', padding: '1.5rem', background: 'white', borderRadius: '12px'}}>
                <div style={{fontSize: '2rem', marginBottom: '0.5rem'}}>✓</div>
                <div style={{fontSize: '0.875rem', fontWeight: '600', color: '#0f172a'}}>TCPA Compliant</div>
              </div>
              <div style={{textAlign: 'center', padding: '1.5rem', background: 'white', borderRadius: '12px'}}>
                <div style={{fontSize: '2rem', marginBottom: '0.5rem'}}>⚡</div>
                <div style={{fontSize: '0.875rem', fontWeight: '600', color: '#0f172a'}}>24hr Response</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
